/**
 * Feedback API Service
 *
 * API client functions for fetching and managing AI-generated feedback
 * on reflections. Includes functions for retrieving feedback and
 * triggering an async regeneration.
 *
 * Regeneration is asynchronous: the POST endpoint returns 202 with the
 * current `ReflectionFeedbackResponse` (now stamped
 * `regeneration_in_progress=true`). The frontend polls the GET endpoint
 * via React Query's `refetchInterval` until the flag flips back to false.
 */

import { apiClient } from "./api";
import type {
  ReflectionFeedbackResponse,
  RegenerateFeedbackRequest,
} from "@/types/reflection";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Get feedback for a specific reflection
 *
 * Returns the AI-generated feedback including the therapy style used
 * and a server-derived `regeneration_in_progress` flag.
 */
export async function getReflectionFeedback(
  reflectionId: string
): Promise<ReflectionFeedbackResponse> {
  const response = await apiClient.get<ReflectionFeedbackResponse>(
    `${API_PREFIX}/reflections/${reflectionId}/feedback`
  );
  return response.data;
}

/**
 * Regenerate feedback for a specific reflection
 *
 * Queues a background task to regenerate feedback with the specified
 * therapy style. Returns 202 immediately with the existing row stamped
 * `regeneration_in_progress=true`. Callers should then rely on the
 * polling GET to detect completion.
 */
export async function regenerateFeedback(
  reflectionId: string,
  therapyStyleId?: string
): Promise<ReflectionFeedbackResponse> {
  const payload: RegenerateFeedbackRequest = {};
  if (therapyStyleId) {
    payload.therapy_style_id = therapyStyleId;
  }

  const response = await apiClient.post<ReflectionFeedbackResponse>(
    `${API_PREFIX}/reflections/${reflectionId}/feedback/regenerate`,
    payload
  );
  return response.data;
}
